
import React from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import './App.css';
import linkedinIcon from './linkedin.svg';
import youtubeIcon from './youtube.svg';
import xIcon from './x.svg';
import entraIcon from './Entra.svg';
import intuneIcon from './intune.svg';
import graphIcon from './Graph.svg';
import m365Icon from './Microsoft365.svg';
import defenderIcon from './Defender.svg';

// Import components
import Homepage from './components/Homepage';
import TabNavigation from './components/TabNavigation';
import EntraDocs from './components/EntraDocs';
import IntuneDocs from './components/IntuneDocs';
import GraphDocs from './components/GraphDocs';
import M365Docs from './components/M365Docs';
import DefenderDocs from './components/DefenderDocs';
import ServiceTracking from './components/ServiceTracking';
import About from './components/About';

const SITE_URL = 'https://microsoftdocstracker.com';

const PAGE_META = {
  home: {
    path: '/',
    title: 'MicrosoftDocsTracker — Track Microsoft Documentation Changes',
    description: 'Stay effortlessly up-to-date with all changes to Microsoft documentation. MicrosoftDocsTracker monitors Entra ID, Intune, Graph API, M365 and Defender updates so you never miss a critical change.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'MicrosoftDocsTracker',
      url: SITE_URL,
      description: 'Track changes to Microsoft Entra ID, Intune, Graph API, Microsoft 365 and Defender documentation.',
      publisher: { '@type': 'Organization', name: 'Our Cloud Network', url: 'https://ourcloudnetwork.com' },
    },
  },
  about: {
    path: '/about',
    title: 'About — MicrosoftDocsTracker',
    description: 'Learn about MicrosoftDocsTracker, the tool that monitors Microsoft documentation changes for Entra ID, Intune, Graph API, Microsoft 365 and Defender.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'About MicrosoftDocsTracker',
      url: `${SITE_URL}/about`,
      description: 'Learn about MicrosoftDocsTracker and how it monitors Microsoft documentation changes.',
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'MicrosoftDocsTracker' },
    },
  },
  entra: {
    path: '/entra',
    title: 'Microsoft Entra ID Documentation Changes — MicrosoftDocsTracker',
    description: 'Track the latest changes to Microsoft Entra ID documentation. Stay informed about identity and access management updates.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Microsoft Entra ID Documentation Changes',
      url: `${SITE_URL}/entra`,
      description: 'Track the latest changes to Microsoft Entra ID documentation.',
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'MicrosoftDocsTracker' },
      about: { '@type': 'SoftwareApplication', name: 'Microsoft Entra ID', applicationCategory: 'SecurityApplication' },
    },
  },
  intune: {
    path: '/intune',
    title: 'Microsoft Intune Documentation Changes — MicrosoftDocsTracker',
    description: 'Track the latest changes to Microsoft Intune documentation. Stay on top of endpoint management and device policy updates.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Microsoft Intune Documentation Changes',
      url: `${SITE_URL}/intune`,
      description: 'Track the latest changes to Microsoft Intune documentation.',
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'MicrosoftDocsTracker' },
      about: { '@type': 'SoftwareApplication', name: 'Microsoft Intune', applicationCategory: 'BusinessApplication' },
    },
  },
  graph: {
    path: '/graph',
    title: 'Microsoft Graph API Documentation Changes — MicrosoftDocsTracker',
    description: 'Track the latest changes to Microsoft Graph API documentation. Stay current with API updates, new endpoints and deprecations.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Microsoft Graph API Documentation Changes',
      url: `${SITE_URL}/graph`,
      description: 'Track the latest changes to Microsoft Graph API documentation.',
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'MicrosoftDocsTracker' },
      about: { '@type': 'SoftwareApplication', name: 'Microsoft Graph API', applicationCategory: 'DeveloperApplication' },
    },
  },
  m365: {
    path: '/m365',
    title: 'Microsoft 365 Documentation Changes — MicrosoftDocsTracker',
    description: 'Track the latest changes to Microsoft 365 documentation including Teams, SharePoint, Exchange and more.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Microsoft 365 Documentation Changes',
      url: `${SITE_URL}/m365`,
      description: 'Track the latest changes to Microsoft 365 documentation.',
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'MicrosoftDocsTracker' },
      about: { '@type': 'SoftwareApplication', name: 'Microsoft 365', applicationCategory: 'BusinessApplication' },
    },
  },
  defender: {
    path: '/defender',
    title: 'Microsoft Defender Documentation Changes — MicrosoftDocsTracker',
    description: 'Track the latest changes to Microsoft Defender documentation. Stay informed about security updates, new features and threat protection changes.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Microsoft Defender Documentation Changes',
      url: `${SITE_URL}/defender`,
      description: 'Track the latest changes to Microsoft Defender documentation.',
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'MicrosoftDocsTracker' },
      about: { '@type': 'SoftwareApplication', name: 'Microsoft Defender', applicationCategory: 'SecurityApplication' },
    },
  },
  'service-tracking': {
    path: '/service-tracking',
    title: 'Microsoft Features — MicrosoftDocsTracker',
    description: 'Track Microsoft service features and their rollout status across Entra ID, Intune, Defender, Graph API and Microsoft 365.',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Microsoft Features',
      url: `${SITE_URL}/service-tracking`,
      description: 'Track Microsoft service features and their rollout status.',
      isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'MicrosoftDocsTracker' },
    },
  },
};

const socials = [
  {
    href: 'https://www.linkedin.com/in/danielbradley2/',
    label: 'LinkedIn',
    icon: <img src={linkedinIcon} alt="LinkedIn" width="24" height="24" />,
  },
  {
    href: 'https://x.com/DanielatOCN',
    label: 'X',
    icon: <img src={xIcon} alt="X" width="24" height="24" />,
  },
  {
    href: 'https://www.youtube.com/@ourcloudnetwork',
    label: 'YouTube',
    icon: <img src={youtubeIcon} alt="YouTube" width="24" height="24" />,
  },
];

const tabs = [
  { id: 'home',            label: 'Home',            icon: '🏠',                                                                              path: '/' },
  { id: 'about',           label: 'About',           icon: '👤',                                                                              path: '/about' },
  { id: 'entra',           label: 'Entra ID',        icon: <img src={entraIcon}    alt="Entra ID"       width="20" height="20" />,            path: '/entra' },
  { id: 'intune',          label: 'Intune',          icon: <img src={intuneIcon}   alt="Intune"         width="20" height="20" />,            path: '/intune' },
  { id: 'graph',           label: 'Graph API',       icon: <img src={graphIcon}    alt="Graph API"      width="20" height="20" />,            path: '/graph' },
  { id: 'm365',            label: 'M365',            icon: <img src={m365Icon}     alt="Microsoft 365"  width="20" height="20" />,            path: '/m365' },
  { id: 'defender',        label: 'Defender',        icon: <img src={defenderIcon} alt="Defender"       width="20" height="20" />,            path: '/defender' },
  { id: 'service-tracking',label: 'Features',           icon: <span style={{fontSize: '18px', lineHeight: 1}}>📋</span>,                           path: '/service-tracking' },
];

function PageHelmet({ pageKey }) {
  const meta = PAGE_META[pageKey] || PAGE_META.home;
  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={`${SITE_URL}${meta.path}`} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={`${SITE_URL}${meta.path}`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <script type="application/ld+json">{JSON.stringify(meta.jsonLd)}</script>
    </Helmet>
  );
}

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveTab = () => {
    const path = location.pathname.replace(/^\//, '') || 'home';
    return path;
  };

  const handleTabChange = (tabId) => {
    navigate(tabId === 'home' ? '/' : `/${tabId}`);
  };

  const activeTab = getActiveTab();

  return (
    <div className="App">
      <PageHelmet pageKey={activeTab} />
      <header className="ed-header">
        <div className="ed-header-content">
          <div className="ed-title-section">
            <h1 className="ed-title">
              <span className="ed-title-main">
                MICROSOFT<span className="ed-title-highlight">DOCS</span>TRACKER
              </span>
              <span className="ed-title-subtitle">
                by <a href="https://ourcloudnetwork.com/" target="_blank" rel="noopener noreferrer" className="ed-title-link">ourcloudnetwork.com</a>
              </span>
            </h1>
          </div>
        </div>
      </header>

      <div className="ed-creator-bar">
        <span className="ed-creator-text">FOLLOW:</span>
        <span className="ed-socials">
          {socials.map(s => (
            <a key={s.href} href={s.href} className="ed-social-link" target="_blank" rel="noopener noreferrer" title={s.label} aria-label={s.label}>
              {s.icon}
            </a>
          ))}
        </span>
      </div>

      <TabNavigation
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={tabs}
      />

      <main className="ed-main">
        <Routes>
          <Route path="/"                element={<Homepage onNavigateToTab={handleTabChange} />} />
          <Route path="/about"           element={<About />} />
          <Route path="/entra"           element={<EntraDocs />} />
          <Route path="/intune"          element={<IntuneDocs />} />
          <Route path="/graph"           element={<GraphDocs />} />
          <Route path="/m365"            element={<M365Docs />} />
          <Route path="/defender"        element={<DefenderDocs />} />
          <Route path="/service-tracking" element={<ServiceTracking />} />
          <Route path="*"               element={<Homepage onNavigateToTab={handleTabChange} />} />
        </Routes>
      </main>
      <footer className="ed-footer">
        <span>Copyright © {new Date().getFullYear()} MicrosoftDocsTracker | Powered by <a href="https://ourcloudnetwork.com/" target="_blank" rel="noopener noreferrer">ourcloudnetwork.com</a></span>
      </footer>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
